import { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth';
import { Card, CardHeader, CardTitle, CardSubtitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { toIsoDate } from '@/lib/calendar';
import { useSessions } from '@/api/sessions';
import { useClientsCounts } from '@/api/clients';
import { useClaimStats } from '@/api/claims';
import { useMyProfile } from '@/api/users';
import { usePendingSignaturesSummary } from '@/api/portal';
import { useSettings } from '@/api/settings';
import { useClientTrends, type TrendWindow } from '@/api/dashboardTrends';
import {
    grantsAssignedClients,
    grantsViewAllClients,
} from '@/features/users/permissions';
import { KpiCard } from './KpiCard';
import { ClientTrendsGadget } from './ClientTrendsGadget';
import { TodayTimeline } from './TodayTimeline';
import { AlertsPanel } from './AlertsPanel';
import { DayOfWeekChart } from './DayOfWeekChart';
import { Skeleton } from '@/components/ui/Skeleton';
import { Calendar, Users, FileText } from 'lucide-react';
import {
    buildAlerts,
    countTodaySessions,
    countNotesNeedingAttention,
    countClients,
    countClaimsNeedingAttention,
    dayOfWeekCounts,
} from './selectors';

function startOfWindow(todayIso: string, days: number): string {
    const d = new Date(`${todayIso}T00:00:00`);
    d.setDate(d.getDate() - (days - 1));
    return toIsoDate(d);
}

export function DashboardPage() {
    const user = useAuthStore((s) => s.user);
    const navigate = useNavigate();

    const perms = useMemo(() => new Set(user?.permissions ?? []), [user]);
    const hasViewAll = grantsViewAllClients(perms);
    const hasAssigned = grantsAssignedClients(perms);
    const hasScheduling = perms.has('view_scheduling');
    const hasNotes = perms.has('view_notes');
    const hasBilling = perms.has('view_billing');

    const todayIso = toIsoDate(new Date());
    const last30Start = startOfWindow(todayIso, 30);

    // Parents are redirected to the portal below; the guard here just keeps
    // the trends fetch (which would 403 for them) from firing first.
    const [trendWindow, setTrendWindow] = useState<TrendWindow>(14);
    const trendsQuery = useClientTrends(trendWindow, {
        enabled: hasAssigned && user?.roleType !== 'parent',
    });

    const sessionsQuery = useSessions(
        hasScheduling ? { dateFrom: last30Start, dateTo: todayIso } : undefined,
    );
    const clientsQuery = useClientsCounts();
    const claimStatsQuery = useClaimStats();
    const myProfileQuery = useMyProfile();
    const pendingSigsQuery = usePendingSignaturesSummary({ enabled: hasNotes });
    const settingsQuery = useSettings();

    const sessions = hasScheduling ? sessionsQuery.data : undefined;
    const countsData = hasAssigned ? clientsQuery.data : undefined;
    const claimStats = hasBilling ? claimStatsQuery.data : undefined;
    const myProfile = myProfileQuery.data;
    const pendingSigs = hasNotes ? pendingSigsQuery.data : undefined;
    const settings = settingsQuery.data;

    // Selectors walk session/client/claim arrays; memoize so a re-render
    // triggered by an unrelated piece of dashboard state (e.g., a settings
    // refetch) doesn't rewalk the lists for every KPI card.
    const todayKpi = useMemo(() => countTodaySessions(sessions, todayIso), [sessions, todayIso]);
    const notesKpi = useMemo(() => countNotesNeedingAttention(sessions), [sessions]);
    const clientKpi = useMemo(() => countClients(countsData), [countsData]);
    const claimsKpi = useMemo(() => countClaimsNeedingAttention(claimStats), [claimStats]);
    const dowCounts = useMemo(
        () => dayOfWeekCounts(sessions, last30Start, todayIso),
        [sessions, last30Start, todayIso],
    );

    const alerts = useMemo(
        () =>
            buildAlerts({
                sessions,
                clients: countsData?.authRows,
                claimStats,
                myProfile,
                pendingSigs,
                settings: {
                    billing: settings?.billing,
                    notification: settings?.notification,
                },
                perms,
                todayIso,
            }),
        [sessions, countsData, claimStats, myProfile, pendingSigs, settings, perms, todayIso],
    );

    const todaySessions = useMemo(
        () => (sessions ?? []).filter((s) => s.date === todayIso),
        [sessions, todayIso],
    );

    // Parent accounts go straight to the portal — checked after all hooks
    // so the Rules of Hooks are never violated.
    if (user?.roleType === 'parent') {
        return <Navigate to="/portal" replace />;
    }

    // True while any query the dashboard depends on is still fetching.
    const isLoading =
        (hasScheduling && sessionsQuery.isLoading) ||
        (hasAssigned && clientsQuery.isLoading) ||
        (hasBilling && claimStatsQuery.isLoading);

    const showToday = hasScheduling;
    const showNotesKpi = hasNotes && hasScheduling;
    const showClientsKpi = hasAssigned;
    const showClaimsKpi = hasBilling;
    const showTimeline = hasScheduling;
    const showDowChart = hasScheduling;
    const showAlerts = alerts.length > 0;
    // Hidden entirely for users with no assigned caseload (e.g. billing-only
    // roles) — an empty trends panel is noise for them, not information.
    const showTrends =
        hasAssigned &&
        (trendsQuery.isLoading || (trendsQuery.data?.clients.length ?? 0) > 0);

    const hasAnyWidget =
        showToday || showNotesKpi || showClientsKpi || showClaimsKpi
        || showTimeline || showDowChart || showAlerts;

    // New-practice empty state: scheduling + clients both loaded with zero data.
    const isNewPractice =
        !isLoading &&
        hasAnyWidget &&
        (sessions ?? []).length === 0 &&
        (countsData?.total ?? 0) === 0 &&
        !claimStats?.total;

    return (
        <div className="p-8 max-w-6xl mx-auto">
            <h1 className="font-display text-2xl font-bold text-text mb-1">
                Welcome, {user?.first}!
            </h1>
            <p className="text-xs text-text-subtle mb-6">{user?.roleName}</p>

            {!hasAnyWidget ? (
                <Card>
                    <CardHeader>
                        <div>
                            <CardTitle>Dashboard</CardTitle>
                            <CardSubtitle>
                                Use the sidebar to navigate to the modules you have access to.
                            </CardSubtitle>
                        </div>
                    </CardHeader>
                </Card>
            ) : (
                <div className="flex flex-col gap-6">
                    {(showToday || showNotesKpi || showClientsKpi || showClaimsKpi) && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            {isLoading ? (
                                Array.from({ length: 4 }).map((_, i) => (
                                    <div key={i} className="bg-surface border border-border rounded-lg p-4 shadow-sm flex flex-col gap-2">
                                        <Skeleton className="h-3 w-24" />
                                        <Skeleton className="h-8 w-12" />
                                        <Skeleton className="h-3 w-32" />
                                    </div>
                                ))
                            ) : (
                                <>
                                    {showToday && (
                                        <KpiCard
                                            label="Today's sessions"
                                            value={todayKpi.total}
                                            sub={
                                                todayKpi.total === 0
                                                    ? 'No sessions today'
                                                    : `${todayKpi.complete} done · ${todayKpi.remaining} upcoming${todayKpi.cancelled ? ` · ${todayKpi.cancelled} cancelled` : ''}`
                                            }
                                            to="/scheduling"
                                        />
                                    )}
                                    {showNotesKpi && (
                                        <KpiCard
                                            label="Notes to action"
                                            value={notesKpi.total}
                                            tone={notesKpi.missing > 0 ? 'danger' : 'neutral'}
                                            sub={
                                                notesKpi.total === 0
                                                    ? 'All caught up'
                                                    : `${notesKpi.missing} missing · ${notesKpi.drafting} drafting · ${notesKpi.ready} to finalize`
                                            }
                                            to="/scheduling"
                                        />
                                    )}
                                    {showClientsKpi && (
                                        <KpiCard
                                            label={hasViewAll ? 'Active clients' : 'Assigned clients'}
                                            value={clientKpi.active}
                                            sub={
                                                hasViewAll && clientKpi.total !== clientKpi.active
                                                    ? `${clientKpi.total} total`
                                                    : undefined
                                            }
                                            to="/clients"
                                        />
                                    )}
                                    {showClaimsKpi && (
                                        <KpiCard
                                            label="Claims to action"
                                            value={claimsKpi.attention}
                                            tone={claimsKpi.denied > 0 ? 'danger' : 'neutral'}
                                            sub={
                                                claimsKpi.attention === 0
                                                    ? 'Queue clear'
                                                    : `${claimsKpi.denied} denied · ${claimsKpi.ready} ready`
                                            }
                                            to="/billing"
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {showTimeline && <TodayTimeline sessions={todaySessions} />}
                        <AlertsPanel alerts={alerts} />
                    </div>

                    {showTrends && (
                        <ClientTrendsGadget
                            clients={trendsQuery.data?.clients}
                            windowDays={trendWindow}
                            onWindowChange={setTrendWindow}
                            isLoading={trendsQuery.isLoading}
                        />
                    )}

                    {showDowChart && <DayOfWeekChart counts={dowCounts} />}

                    {isNewPractice && (
                        <Card>
                            <div className="p-8 flex flex-col items-center gap-4 text-center">
                                <div className="flex gap-4 text-text-subtle">
                                    <Users className="w-8 h-8" aria-hidden />
                                    <Calendar className="w-8 h-8" aria-hidden />
                                    <FileText className="w-8 h-8" aria-hidden />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-text mb-1">Welcome to GoodSteward!</p>
                                    <p className="text-xs text-text-subtle">Get started by adding your first client, then schedule a session.</p>
                                </div>
                                <div className="flex gap-2">
                                    {hasAssigned && (
                                        <Button variant="primary" size="sm" onClick={() => navigate('/clients/new')}>
                                            <Users className="w-3.5 h-3.5" /> Add first client
                                        </Button>
                                    )}
                                    {hasScheduling && (
                                        <Button variant="ghost" size="sm" onClick={() => navigate('/scheduling')}>
                                            <Calendar className="w-3.5 h-3.5" /> Go to scheduling
                                        </Button>
                                    )}
                                </div>
                            </div>
                        </Card>
                    )}
                </div>
            )}
        </div>
    );
}
